import { Check, ChevronDown, MapPin } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { jurisdictionSelectMessages } from '@/components/ui/jurisdiction-select/JurisdictionSelect.messages';
import { normalizeSubdivisionCode } from '@/components/ui/jurisdiction-select/jurisdictionLocalization';
import {
  getSubdivisionByCode,
  getSubdivisionsForCountry,
} from '@/components/ui/jurisdiction-select/subdivisions';
import { useLocalizedSubdivisionName } from '@/components/ui/jurisdiction-select/useLocalizedSubdivisionName';
import {
  PopoverDrawer,
  PopoverDrawerContent,
  PopoverDrawerTrigger,
} from '@/components/ui/monbile-responsive-popover-drawer';
import { useQueryJurisdictions } from '@/hooks/query/useQueryJurisdictions';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { useIso3166 } from '@/lib/iso3166-lazy';
import { Button, type ButtonProps } from '@heidi/design-system/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@heidi/design-system/command';
import { DrawerHeader, DrawerTitle } from '@heidi/design-system/drawer';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { TypographyP2 } from '@heidi/design-system/typography';

import { personalizationSharedMessages } from '../personalization-shared.messages';

interface Subdivision {
  code: string;
  name: string;
  type: string;
}
interface LocalizedSubdivision extends Subdivision {
  localizedName: string;
}
interface OnboardingJurisdictionSelectProps {
  countryName?: string | null;
  value?: string | null;
  onChange: (jurisdictionCode: string) => void;
  className?: string;
  compact?: boolean;
  size?: ButtonProps['size'];
  subdivisionLabel?: string;
  drawerTitle?: string;
}

export const OnboardingJurisdictionSelect = ({
  countryName,
  value,
  onChange,
  className,
  compact = false,
  size = 'lg',
  subdivisionLabel,
  drawerTitle,
}: OnboardingJurisdictionSelectProps) => {
  const intl = useIntl();
  const getLocalizedSubdivisionName = useLocalizedSubdivisionName();
  const isMobile = useIsMobile();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data: backendJurisdictionOptions = [] } = useQueryJurisdictions(countryName);
  // Lazy-load `iso-3166-2` on mount; included in deps below so memoised
  // values recompute when the module finishes loading. ARC-194.
  const iso3166Module = useIso3166();

  const backendSubdivisions = useMemo<Subdivision[]>(
    () =>
      backendJurisdictionOptions
        .map((option) => {
          const normalizedCode = normalizeSubdivisionCode({
            countryName,
            code: option.code,
          });
          return {
            code: normalizedCode,
            name: option.name,
            type: option.type ?? 'State',
          };
        })
        .filter(
          (subdivision, index, allSubdivisions) =>
            allSubdivisions.findIndex((s) => s.code === subdivision.code) === index
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [backendJurisdictionOptions, countryName]
  );

  const subdivisions = useMemo(
    () =>
      backendSubdivisions.length > 0
        ? backendSubdivisions
        : getSubdivisionsForCountry(countryName, iso3166Module),
    [backendSubdivisions, countryName, iso3166Module]
  );

  const localizedSubdivisions = useMemo<LocalizedSubdivision[]>(
    () =>
      subdivisions.map((subdivision) => ({
        ...subdivision,
        localizedName: getLocalizedSubdivisionName(subdivision),
      })),
    [subdivisions, getLocalizedSubdivisionName]
  );

  const filteredSubdivisions = useMemo(
    () =>
      localizedSubdivisions.filter(
        (subdivision) =>
          subdivision.localizedName.toLowerCase().includes(search.toLowerCase()) ||
          subdivision.name.toLowerCase().includes(search.toLowerCase())
      ),
    [localizedSubdivisions, search]
  );

  const selectedSubdivision = useMemo<LocalizedSubdivision | null>(() => {
    if (!value) return null;

    const selectedFromCountry = localizedSubdivisions.find(
      (subdivision) => subdivision.code === value
    );
    if (selectedFromCountry) return selectedFromCountry;

    const fallbackSubdivision = getSubdivisionByCode(value, countryName, iso3166Module);
    if (!fallbackSubdivision) return null;

    return {
      ...fallbackSubdivision,
      localizedName: getLocalizedSubdivisionName(fallbackSubdivision),
    };
  }, [
    value,
    localizedSubdivisions,
    countryName,
    getLocalizedSubdivisionName,
    iso3166Module,
  ]);

  const selectPlaceholder = subdivisionLabel
    ? intl.formatMessage(jurisdictionSelectMessages.selectSubdivision, {
        label: subdivisionLabel.toLowerCase(),
      })
    : intl.formatMessage(jurisdictionSelectMessages.selectStateRegion);

  const displayName = selectedSubdivision?.localizedName || selectPlaceholder;

  const handleChange = useCallback(
    (code: string) => {
      onChange(code);
      setPopoverOpen(false);
      setSearch('');
    },
    [onChange]
  );

  const disabledPlaceholder = !countryName
    ? intl.formatMessage(personalizationSharedMessages.selectCountryFirst)
    : subdivisions.length === 0
      ? intl.formatMessage(personalizationSharedMessages.noRegionsAvailable)
      : null;

  if (disabledPlaceholder) {
    return (
      <Button
        variant="outline"
        size={size}
        className={cn(
          'w-full justify-between font-normal text-text-secondary',
          className
        )}
        disabled
      >
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <MapPin size={16} className="shrink-0" />
          {!compact && <span className="truncate">{disabledPlaceholder}</span>}
        </div>
        <ChevronDown className="ms-2 size-4 shrink-0 opacity-50" />
      </Button>
    );
  }

  return (
    <PopoverDrawer open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverDrawerTrigger asChild>
        <Button
          variant="outline"
          size={size}
          className={cn(
            'w-full justify-between',
            !selectedSubdivision && 'text-text-secondary',
            className
          )}
          role="combobox"
          aria-expanded={popoverOpen}
        >
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            <MapPin size={16} className="shrink-0" />
            {!compact && <TypographyP2 className="truncate">{displayName}</TypographyP2>}
          </div>
          <ChevronDown className="ms-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverDrawerTrigger>
      <PopoverDrawerContent
        className={
          isMobile
            ? 'px-0 pb-4'
            : 'max-h-[--radix-popover-content-available-height] w-auto min-w-[--radix-popover-trigger-width] max-w-[min(24rem,calc(100vw-2rem))] p-0'
        }
        sideOffset={4}
        align="start"
        side="bottom"
      >
        {isMobile && (
          <DrawerHeader>
            <DrawerTitle>{drawerTitle ?? selectPlaceholder}</DrawerTitle>
          </DrawerHeader>
        )}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={
              subdivisionLabel
                ? intl.formatMessage(jurisdictionSelectMessages.searchSubdivision, {
                    label: subdivisionLabel.toLowerCase(),
                  })
                : intl.formatMessage(jurisdictionSelectMessages.searchStateRegion)
            }
            onValueChange={setSearch}
          />
          <CommandEmpty>
            {intl.formatMessage(personalizationSharedMessages.noResultsFound)}
          </CommandEmpty>
          <ScrollArea
            className={
              isMobile
                ? 'max-h-[60vh] overflow-auto'
                : 'max-h-[220px] overflow-scroll sm:overflow-auto'
            }
          >
            <CommandGroup>
              {filteredSubdivisions.map((subdivision) => (
                <CommandItem
                  key={subdivision.code}
                  value={subdivision.code}
                  onSelect={() => handleChange(subdivision.code)}
                  className="flex gap-3"
                >
                  <Check
                    className={cn(
                      'size-4 shrink-0',
                      value === subdivision.code ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {subdivision.localizedName}
                </CommandItem>
              ))}
            </CommandGroup>
          </ScrollArea>
        </Command>
      </PopoverDrawerContent>
    </PopoverDrawer>
  );
};
